import en from "../../../LogbookApp/Language/en.json"
import de from "../../../LogbookApp/Language/de.json"
import fr from "../../../LogbookApp/Language/fr.json"
import {
  lookupTranslation,
  replaceTokens,
  resolvePreferredLanguage,
} from "app-base/runtime/localization"
const enMessages: LocalizationMessages = require("../../../LogbookApp/Language/en.yaml"); // eslint-disable-line @typescript-eslint/no-require-imports -- Parcel loads YAML via require; no ambient *.yaml type
const deMessages: LocalizationMessages = require("../../../LogbookApp/Language/de.yaml"); // eslint-disable-line @typescript-eslint/no-require-imports -- Parcel loads YAML via require; no ambient *.yaml type
const frMessages: LocalizationMessages = require("../../../LogbookApp/Language/fr.yaml"); // eslint-disable-line @typescript-eslint/no-require-imports -- Parcel loads YAML via require; no ambient *.yaml type

export interface LanguageTextReplacements {
  [name: string]: string | number | undefined;
}

export interface LanguageTextReplacementFunction {
  (text: string, replace?: LanguageTextReplacements): string;
}

export interface LocalizationText {
  [name: string]: string;
}

export interface LocalizationTexts {
  [name: string]: LocalizationText;
}

export interface AlertMessage {
  alarm: string;
  ok?: string;
  warning: string;
}

export interface AlertLightsMessages {
  warning: string;
}

export interface AlertManOverboardMessages {
  emitterActivated: string;
  manualButtonActivated: string;
  manual: string;
  unknown: string;
}

export interface AlertMessages {
  anchorDrift: AlertMessage;
  depth: AlertMessage;
  lights: AlertLightsMessages;
  lostPosition: AlertLostPositionMessages;
  manOverboard: AlertManOverboardMessages;
  timeShift: AlertTimeShiftMessages;
}

export interface AnchorMonitorExplanationMessages {
  fixedRadius: string;
  rodeScope: string;
  sector: string;
}

export interface AnchorMonitorMessages {
  explanation: AnchorMonitorExplanationMessages;
}

// Lost-position messages are keyed by the reason,
// not the alarm/warning severity, so it carries its own shape rather than
// AlertMessage.
export interface AlertLostPositionMessages {
  disconnected: string;
  invalidFix: string;
  jump: string;
  stale: string;
}

export interface AlertTimeShiftMessages {
  plus: string;
  minus: string;
}

export interface ChangeLogTexts {
  [version: string]: string;
}

export interface NoteTexts {
  [name: string]: string;

}

export interface CurrentViewTexts {
  demoWillStart: string;
  firstTrip: string;
  confirmationDialog: CurrentViewConfirmationDialogTexts;
}

export interface CurrentViewConfirmationDialogTexts {
  [name: string]: CurrentViewDialogTexts;
}

export interface CurrentViewDialogTexts {
  [name: string]: string;
}

export interface MailerTemplateMessages {
  subject: string;
  body: string;
  footer: string;
}

export interface MailerMessages {
  footer: string;
  templates: { [name: string]: MailerTemplateMessages };
}

export interface LocalizationMessages {
  shipSetting: NoteTexts;
  alert: AlertMessages;
  anchorMonitor: AnchorMonitorMessages;
  changeLog: ChangeLogTexts;
  connection: NoteTexts;
  currentView: CurrentViewTexts;
  importShip: NoteTexts;
  importTrips: NoteTexts;
  noteText: NoteTexts;
  shipClose: NoteTexts;
  shipInfo: NoteTexts;
  login: NoteTexts;
  logout: NoteTexts;
  mailer?: MailerMessages;
  nmeaSettings: NoteTexts;
  planDescriptions: NoteTexts;
  sessionExpired: NoteTexts;
  subscription: NoteTexts;
}

export const LocalizationReplaceText: LanguageTextReplacementFunction = (text: string, replace?: LanguageTextReplacements): string => {
  const values = Object.fromEntries(
    Object.entries(replace ?? {}).filter(([, value]) => value !== undefined && value !== null),
  );
  return replaceTokens(text, values);
};

export const Localization = {
  // The languages the app ships translations for. Mirrors
  // `LogbookLanguage.supported` (Sources/LogbookFoundation/Language.swift), the
  // Apple bundles' `CFBundleLocalizations`, the appx `<Resource Language>`
  // entries and Android's `locales_config.xml` — adding a language means
  // touching all of them.
  Supported: ["en", "de", "fr"],
  Fallback: "en",
  Languages: {
    en: en as LocalizationText,
    de: de as LocalizationText,
    fr: fr as LocalizationText,
  } as LocalizationTexts,
  Messages: {
    en: enMessages as LocalizationMessages,
    de: deMessages as LocalizationMessages,
    fr: frMessages as LocalizationMessages,
  } as { [language: string]: LocalizationMessages },
  // Resolves a list of locale identifiers (BCP-47 `de-DE`, or Java's `de_DE`) to
  // a language we can serve, or `undefined` when none of them is supported.
  //
  // Walks the list in order and takes the first entry whose *base* subtag is
  // supported, so a device set to Italian with German second gets German rather
  // than the English fallback. The Swift counterpart is
  // `LogbookLanguage.resolve`; both exist because the native shells resolve
  // before injecting, and the web app re-validates whatever arrives.
  resolve: function (preferred: (string | undefined)[]): string | undefined {
    const unsupported = "__unsupported__";
    const resolved = resolvePreferredLanguage(
      preferred
        .filter((tag): tag is string => tag !== undefined)
        .map((tag) => tag.trim().replace("_", "-").toLowerCase()),
      [...Localization.Supported, unsupported],
      unsupported,
    );
    return resolved === unsupported ? undefined : resolved;
  },
  t: function (language: string, text: string, replace?: LanguageTextReplacements): string {
    return LocalizationReplaceText(lookupTranslation(text, {
      language,
      fallbackLanguage: Localization.Fallback,
      dictionaries: Localization.Languages,
      missing: text,
    }), replace);
  },
};
